import { Link } from "react-router-dom";
import { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Phone, MapPin, Calendar, ChevronRight, Dumbbell, CheckCircle, AlertCircle, XCircle, Cake, Trash2, Loader2, Eye, EyeOff, FileText, User, TrendingUp, AlertTriangle } from "lucide-react";
import { getInitials, formatDate, calculateAge } from "../lib/utils";
import { patientApi } from "../services/api";
import { toast } from "sonner";

// Iconițe calificativ final din Referat
const CONCLUZIE_ICONS = {
  APT: { Icon: CheckCircle, cls: "text-emerald-600", title: "APT antrenamente și competiții" },
  APT_SUPRAVEGHERE: { Icon: AlertCircle, cls: "text-amber-600", title: "APT antrenamente și competiții sub supraveghere medicală" },
  APT_INDIVIDUALIZAT: { Icon: AlertCircle, cls: "text-orange-600", title: "APT antrenamente individualizate sub supraveghere" },
  APT_CONDITIONAT: { Icon: AlertCircle, cls: "text-orange-500", title: "APT condiționat (legacy)" },
  INAPT_TEMPORAR: { Icon: XCircle, cls: "text-red-500", title: "INAPT temporar" },
  INAPT: { Icon: XCircle, cls: "text-slate-900", title: "INAPT (legacy)" },
};

export const PatientCard = ({ patient, onDeleted, onUpdated }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingCitit, setTogglingCitit] = useState(false);
  const [citit, setCitit] = useState(!!patient.citit);
  const reviewCount = patient.reviews?.length || 0;
  const dp = patient.date_personale || {};
  const is = patient.info_sportive || {};
  const numarOrdine = patient.numar_ordine || 0;

  const fullName = `${dp.nume || ''} ${dp.prenume || ''}`.trim() || 'Fără nume';
  const concluzieKey = patient.referat?.concluzie;
  const concluzieInfo = concluzieKey ? CONCLUZIE_ICONS[concluzieKey] : null;
  const age = calculateAge(dp.data_nasterii);

  // Formatare data nașterii DD.MM.YYYY (dacă vine în format ISO YYYY-MM-DD)
  const dataNasteriiFmt = (() => {
    const raw = dp.data_nasterii;
    if (!raw) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return raw;
  })();

  // Detectare „red flags" din Istoric Medical (Antecedente Familiale/Personale + Stare de sănătate).
  // Sunt semnalizate pe card ca să atragă atenția înainte de a intra în fișă.
  const im = patient.istoric_medical || {};
  const redFlags = [];
  if (im.antecedente_familie === "Da" ||
      (im.antecedente_familie && im.antecedente_familie !== "Nu" && im.antecedente_familie !== "Da")) {
    redFlags.push("Antecedente familiale");
  }
  if (im.antecedente_personale === "Da" ||
      (im.antecedente_personale && im.antecedente_personale !== "Nu" && im.antecedente_personale !== "Da")) {
    redFlags.push("Antecedente personale");
  }
  if (im.modificari_sanatate === "Da") redFlags.push("Modificări stare sănătate");
  if (im.accidentari_sportive === "Da") redFlags.push("Accidentări sportive (6 luni)");
  if (im.internari_medicale === "Da") redFlags.push("Internări medicale (6 luni)");
  if (im.tratament_medicamentos === "Da") redFlags.push("Tratament medicamentos");
  const hasRedFlags = redFlags.length > 0;

  const handleToggleCitit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (togglingCitit) return;
    setTogglingCitit(true);
    try {
      const res = await patientApi.toggleCitit(patient.id);
      setCitit(!!res.citit);
      toast.success(res.citit ? "Marcat ca citit" : "Marcaj citit eliminat");
      if (onUpdated) onUpdated(patient.id, { citit: !!res.citit });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la actualizare");
    } finally {
      setTogglingCitit(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await patientApi.delete(patient.id);
      const freed = res?.freed_numar_ordine;
      toast.success(
        freed
          ? `Sportiv șters. ID-ul #${freed} va fi reatribuit următoarei înscrieri.`
          : "Sportivul a fost șters"
      );
      setConfirmOpen(false);
      if (onDeleted) onDeleted(patient.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la ștergerea sportivului");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      data-testid={`patient-card-${patient.id}`}
      data-citit={citit ? "true" : "false"}
      className={`card-hover group ${citit ? "bg-slate-200/70 border-slate-300" : ""}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="avatar-placeholder flex-shrink-0">
            {getInitials(fullName)}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <Badge variant="outline" className="flex-shrink-0 font-mono">
                  #{numarOrdine}
                </Badge>
                {(() => {
                  const chips = [];
                  const dp2 = patient.date_personale || {};
                  const isJunior = !!dp2.juniori;
                  if (isJunior && patient.flag_ekg === "cu") chips.push({ k: "cu-ekg", label: "CU EKG", cls: "bg-emerald-100 text-emerald-800 border-emerald-500" });
                  if (isJunior && patient.flag_ekg === "fara") chips.push({ k: "fara-ekg", label: "FĂRĂ EKG", cls: "bg-red-100 text-red-800 border-red-500" });
                  if (patient.flag_analize === "cu") chips.push({ k: "cu-analize", label: "CU ANALIZE", cls: "bg-emerald-100 text-emerald-800 border-emerald-500" });
                  if (patient.flag_analize === "fara") chips.push({ k: "fara-analize", label: "FĂRĂ ANALIZE", cls: "bg-red-100 text-red-800 border-red-500" });
                  if (patient.flag_analize === "proprii") chips.push({ k: "proprii-analize", label: "ANALIZE PROPRII", cls: "bg-violet-100 text-violet-800 border-violet-500" });
                  if (patient.flag_cas_contract === "cas") chips.push({ k: "cas", label: "CAS", cls: "bg-sky-100 text-sky-800 border-sky-500" });
                  if (patient.flag_cas_contract === "contract") chips.push({ k: "contract", label: "CONTRACT", cls: "bg-amber-100 text-amber-800 border-amber-500" });
                  return chips.map((c) => (
                    <Badge
                      key={c.k}
                      variant="outline"
                      className={`flex-shrink-0 text-[10px] border font-semibold ${c.cls}`}
                      data-testid={`patient-flag-${c.k}-${patient.id}`}
                    >
                      {c.label}
                    </Badge>
                  ));
                })()}
                {patient.numar_viza && (
                  <Badge
                    className="flex-shrink-0 bg-emerald-600 text-white border-emerald-700 font-medium"
                    data-testid={`patient-numar-viza-${patient.id}`}
                    title="Numărul de ordine (viză medicală)"
                  >
                    Nr. ord. {patient.numar_viza}
                  </Badge>
                )}
                <h3 className="font-semibold text-foreground truncate" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                  {fullName}
                </h3>
                {concluzieInfo && (
                  <concluzieInfo.Icon
                    className={`w-5 h-5 flex-shrink-0 ${concluzieInfo.cls}`}
                    title={concluzieInfo.title}
                    data-testid={`patient-concluzie-${patient.id}`}
                  />
                )}
                {hasRedFlags && (
                  <AlertTriangle
                    className="w-5 h-5 flex-shrink-0 text-red-500"
                    title={`Atenție — istoric medical:\n• ${redFlags.join("\n• ")}`}
                    data-testid={`patient-redflag-${patient.id}`}
                  />
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {age !== null && (
                  <Badge
                    variant="outline"
                    className="font-medium border-primary/30 text-primary bg-primary/5"
                    data-testid={`patient-age-${patient.id}`}
                    title={`Născut(ă) ${dp.data_nasterii || "?"}`}
                  >
                    <Cake className="w-3 h-3 mr-1" />
                    {age} ani
                  </Badge>
                )}
                <Badge variant="secondary">
                  {dp.sex || 'N/A'}
                </Badge>
              </div>
            </div>

            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              {dp.cnp && (
                <div className="flex items-center gap-2" data-testid={`patient-cnp-${patient.id}`}>
                  <FileText className="w-3.5 h-3.5" />
                  <span className="truncate font-mono">CNP: {dp.cnp}</span>
                </div>
              )}
              {dataNasteriiFmt && (
                <div className="flex items-center gap-2" data-testid={`patient-data-nasterii-${patient.id}`}>
                  <Cake className="w-3.5 h-3.5" />
                  <span className="truncate">Născut(ă): {dataNasteriiFmt}</span>
                </div>
              )}
              {is.sport_practicat && (
                <div className="flex items-center gap-2">
                  <Dumbbell className="w-3.5 h-3.5" />
                  <span>{is.sport_practicat}</span>
                  {is.club_sportiv && <span className="text-xs">• {is.club_sportiv}</span>}
                </div>
              )}
              {is.antrenor && (
                <div className="flex items-center gap-2" data-testid={`patient-antrenor-${patient.id}`}>
                  <User className="w-3.5 h-3.5" />
                  <span className="truncate">Antrenor: {is.antrenor}</span>
                </div>
              )}
              {is.antrenamente_saptamana && (
                <div className="flex items-center gap-2" data-testid={`patient-antrenamente-${patient.id}`}>
                  <TrendingUp className="w-3.5 h-3.5" />
                  <span className="truncate">Antrenamente/săpt: {is.antrenamente_saptamana}</span>
                </div>
              )}
              {dp.telefon && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="truncate">{dp.telefon}</span>
                </div>
              )}
              {dp.localitate && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="truncate">{dp.localitate}</span>
                </div>
              )}
            </div>

            {/* Sport info preview */}
            {is.post_categorie && (
              <div className="mt-3 p-2 bg-muted rounded-md">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                  Post / Categorie
                </p>
                <p className="text-sm mt-0.5 truncate">
                  {is.post_categorie}
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" />
                <span>{formatDate(patient.created_at)}</span>
                {reviewCount > 0 && (
                  <Badge variant="outline" className="ml-2">
                    {reviewCount} consultați{reviewCount !== 1 ? "i" : "e"}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  data-testid={`toggle-citit-${patient.id}`}
                  variant={citit ? "secondary" : "ghost"}
                  size="sm"
                  className={`gap-1 ${citit ? "bg-slate-500 hover:bg-slate-600 text-white" : "text-slate-600 hover:text-slate-800 hover:bg-slate-100"}`}
                  onClick={handleToggleCitit}
                  disabled={togglingCitit}
                  title={citit ? "Elimină marcajul „citit”" : "Marchează ca citit"}
                >
                  {togglingCitit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : citit ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  {citit ? "Citit" : "Marchează citit"}
                </Button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Button
                    data-testid={`delete-patient-${patient.id}`}
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setConfirmOpen(true);
                    }}
                    title="Șterge sportivul"
                  >
                    <Trash2 className="w-4 h-4" />
                    Șterge
                  </Button>
                  <Link to={`/admin/patients/${patient.id}`}>
                    <Button
                      data-testid={`view-patient-${patient.id}`}
                      variant="ghost"
                      size="sm"
                      className="gap-1"
                    >
                      Vezi
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Confirm delete dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`confirm-delete-${patient.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Șterge sportivul?</AlertDialogTitle>
            <AlertDialogDescription>
              Sportivul <strong>{fullName}</strong> (<span className="font-mono">#{numarOrdine}</span>) va fi șters permanent împreună cu toate fișele și consultațiile.
              <br />
              <span className="text-amber-700 font-medium">ID-ul <span className="font-mono">#{numarOrdine}</span> va fi reatribuit automat următoarei persoane care se înscrie prin formular.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid={`confirm-delete-action-${patient.id}`}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Șterge definitiv
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
